import { router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { Pagination as PaginationMeta } from "@/types/pagination"

/**
 * Server-side pagination controls. `params` is everything else in the query
 * string — the current filters — so paging never silently drops them.
 */
export function Pagination({
  meta,
  path,
  params = {},
  label = "rows",
}: {
  meta: PaginationMeta
  path: string
  params?: Record<string, string | number | null | undefined>
  label?: string
}) {
  if (meta.total_count === 0) return null

  const go = (page: number) => {
    router.get(
      path,
      { ...clean(params), page },
      { preserveState: true, preserveScroll: true },
    )
  }

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <p className="text-xs text-ink-muted">
        Showing {meta.first_item.toLocaleString()}–{meta.last_item.toLocaleString()} of{" "}
        {meta.total_count.toLocaleString()} {label}
      </p>

      {meta.total_pages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={meta.prev_page === null}
            onClick={() => meta.prev_page && go(meta.prev_page)}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Previous
          </Button>
          <span className="text-xs text-ink-muted">
            Page {meta.page.toLocaleString()} of {meta.total_pages.toLocaleString()}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={meta.next_page === null}
            onClick={() => meta.next_page && go(meta.next_page)}
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

/** Drop empty values so they don't end up in the URL as `?from=`. */
export function clean(params: Record<string, string | number | null | undefined>) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  )
}
